import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const renderText = (text) =>
  text.split(/\*\*(.+?)\*\*/g).map((part, i) => (i % 2 === 1 ? <b key={i}>{part}</b> : part));

export const Assistant = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [reportMode, setReportMode] = useState(false);
  const endRef = useRef(null);
  const sessionRef = useRef(sessionStorage.getItem("hub_assistant_session") || "");

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  if (!user) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      if (reportMode) {
        await api.post("/assistant/report", {
          description: text,
          page: location.pathname,
          session_id: sessionRef.current,
        });
        setReportMode(false);
        setMessages((m) => [...m, { role: "assistant", text: "Done — I've emailed that to Marc with your details. He'll pick it up. Anything else?" }]);
      } else {
        const { data } = await api.post("/assistant/chat", {
          message: text,
          session_id: sessionRef.current,
          page: location.pathname,
        });
        sessionRef.current = data.session_id;
        sessionStorage.setItem("hub_assistant_session", data.session_id);
        setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
        if (data.escalated) toast.success("Flagged to Marc by email");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMessages((m) => [...m, { role: "assistant", text: typeof d === "string" ? d : "Sorry — I couldn't reply just now. Try again in a moment." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div className="fixed bottom-24 right-4 sm:right-6 z-50 w-[92vw] max-w-sm rounded-2xl shadow-2xl border border-gray-200 bg-white overflow-hidden flex flex-col" style={{ height: "min(560px, 75vh)" }} data-testid="assistant-panel">
          <div className="bg-[#0A2540] text-white px-4 py-3 flex items-center justify-between">
            <div>
              <div className="font-semibold text-sm">Hub Assistant</div>
              <div className="text-[11px] text-sky-300">How-to help, ideas & problem reporting</div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="assistant-close">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50">
            {messages.length === 0 && (
              <div className="text-sm text-gray-600 bg-white rounded-xl p-3 border border-gray-200">
                Hi {(user.name || "").split(" ")[0] || "there"} — ask me anything about the Hub. e.g. <i>"How do I add photos?"</i>, <i>"Give me ideas for this project"</i>, or report a problem and I'll email Marc.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "ml-auto bg-sky-500 text-white" : "bg-white border border-gray-200 text-gray-800"}`}>
                {renderText(m.text)}
              </div>
            ))}
            {busy && (
              <div className="bg-white border border-gray-200 rounded-xl px-3 py-2 w-14">
                <Loader2 className="h-4 w-4 animate-spin text-sky-500" />
              </div>
            )}
            <div ref={endRef} />
          </div>
          <div className="p-2.5 border-t border-gray-200 bg-white">
            {reportMode && (
              <div className="text-[11px] text-red-600 font-semibold mb-1.5 px-1" data-testid="assistant-report-mode">
                Describe the problem — it goes straight to Marc's email. (<button className="underline" onClick={() => setReportMode(false)}>cancel</button>)
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setReportMode(!reportMode)}
                title="Report a problem to Marc"
                className={`shrink-0 rounded-lg p-2 border ${reportMode ? "bg-red-50 border-red-300 text-red-600" : "border-gray-200 text-gray-400 hover:text-red-500"}`}
                data-testid="assistant-report-btn"
              >
                <LifeBuoy className="h-4 w-4" />
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={reportMode ? "What's gone wrong?" : "Ask me anything…"}
                className="flex-1 text-sm rounded-lg border border-gray-200 px-3 py-2 focus:outline-none focus:border-sky-400"
                data-testid="assistant-input"
              />
              <button onClick={send} disabled={busy || !input.trim()} className="shrink-0 rounded-lg bg-sky-500 hover:bg-sky-600 disabled:opacity-40 text-white p-2" data-testid="assistant-send">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-4 sm:right-6 z-50 rounded-full bg-[#0A2540] hover:bg-[#0E3358] text-white shadow-xl flex items-center gap-2 px-4 py-3.5 transition-transform hover:scale-105"
        data-testid="assistant-fab"
      >
        <MessageCircle className="h-5 w-5 text-sky-400" />
        <span className="text-sm font-semibold hidden sm:inline">Need a hand?</span>
      </button>
    </>
  );
};
